import {AbstractControl, AsyncValidatorFn, FormGroup, ValidationErrors, ValidatorFn} from '@angular/forms';
import {Observable, of, timeout} from 'rxjs';
import {HttpClient, HttpParams} from '@angular/common/http';
import {catchError, map} from 'rxjs/operators';
import {TranslationService} from './services/translation.service';
import {IdentificationData} from "../data/models/identification-data.model";

export interface IdentificationDataFunc{
  (): IdentificationData
}

export class CustomValidators {
  static validateIBAN(http: HttpClient, translationService: TranslationService, swiftBIC: AbstractControl = null, bankName: AbstractControl = null): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors> => {
      if (!control?.value) {
        return of(null);
      }
      if (swiftBIC) { swiftBIC.reset(translationService.getTranslation('filled_automatically')); }
      if (bankName) { bankName.reset(translationService.getTranslation('filled_automatically')); }
      //TODO Erst prüfen wenn IBAN >= 15 ist
      return http.get<any>('/webabo/bank/iban/' + control.value).pipe(
        timeout(15000),
        // andersherum würde map nach catchError trotzdem ausgeführt! (gibt es evtl. eine pipe Function, die nur bei success greift?)
        map( (response) => {
          if (swiftBIC) { swiftBIC.patchValue(response.swiftBIC); }
          if (bankName) { bankName.patchValue(response.bankName); }
          return null;
        }),
        catchError( () => {
          return of({message: translationService.getTranslation('error_invalid_iban')});
        }),
      );
    };
  }


  static checkEmailAddress(http: HttpClient, translationService: TranslationService): AsyncValidatorFn | null {
    return (control: AbstractControl): Observable<ValidationErrors> => {
      if (control && control.value) {
        return CustomValidators.checkIfEmailExists(control.value, http, translationService);
      } else {
        return of(null);
      }
    };
  }

  static validatePhoneNumber(httpClient: HttpClient, translationService: TranslationService, countryCode: string, resultCallback?: (result: any) => void): AsyncValidatorFn | null {
    return (control: AbstractControl): Observable<ValidationErrors> | null => {
      if (!control || !control.value || control.value.trim() === 0) {
        return of(null);
      } else if (control && control.value) {
        return CustomValidators.validatePhone(control, countryCode, httpClient, translationService, resultCallback);
      }
    }
  }

  static validatePhone(phone: any, countryCode: string, httpClient: HttpClient, translationService: TranslationService, resultCallback?: (result: any) => void): Observable<any> {
    const responseBody = {
      fullNumber: phone.value.replace('/', ''),
      countryCode: countryCode
    };

    return httpClient.post<any>('/webabo/users/phone/validate', responseBody).pipe(
      map((response) => {
        if (response && response.formattedNumber) {
          // Das formatierte Ergebnis wird an das Input übergeben
          if (resultCallback) {
            return resultCallback(response.formattedNumber);
          }
          return of(null);
        } else {
          return { message: translationService.getTranslation('phone.wrongFormat') };
        }
      }),
      catchError((error) => {
        return of({ message: translationService.getTranslation('phone.validationError') } );
      }),
    );
  }


  static checkIfEmailExists(email: string, http: HttpClient, translationService: TranslationService): Observable<any> {
    const httpParams = new HttpParams().append("emailAddress", email)
    return http.post<any>('/webabo/auth/check-email', httpParams).pipe(
      timeout(15000),
      // andersherum würde map nach catchError trotzdem ausgeführt! (gibt es evtl. eine pipe Function, die nur bei success greift?)
      map((response) => {
        if (response) {
          throw new Error()
        } else {
          return null;
        }
      }),
      catchError(() => {
        return of({message: translationService.getTranslation('error_email_exists')});
      }),
    );
  }

  static checkIdentificationFieldIsCorrect(fieldIdentifier: string, data: IdentificationDataFunc): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let error = false;
      if (control.touched && control.value && control.value !== '') {
        if (fieldIdentifier === 'EMAIL' && !data().matchEmail) {
          error = true;
        }
        if (fieldIdentifier === 'PHONE' && !data().matchPhone) {
          error = true;
        }
        if (fieldIdentifier === 'BIRTHDAY' && !data().matchBirthday) {
          error = true;
        }
        if (fieldIdentifier === 'CLIENTNO' && !data().matchClientNo) {
          error = true;
        }
        if (fieldIdentifier === 'IBAN' && !data().matchIBAN) {
          error = true;
        }
        if (error) {
          return of({error: true})
        }
      }
      return null;
    }
  }


  static checkRegisterForm(shouldValidatePassword: boolean): ValidatorFn {
    return (group: FormGroup): ValidationErrors | null => {
      const emailField = group.get('email');
      const passwordField = group.get('password');

      if(emailField.invalid) {
        return {error: true};
      }

      if(shouldValidatePassword && (passwordField.invalid || passwordField.value.length <= 0)) {
        return {error: true};
      }

      return null;
    }
  }
}
